import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import RichEditor from "../components/RichEditor";
import RichView from "../components/RichView";
import { toast } from "sonner";
import { FloppyDisk, User as UserIcon, Camera, ShieldCheck, Bell, SpeakerHigh, UploadSimple, X, Key, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast as _toast } from "sonner";
import { isNotificationSoundEnabled, setNotificationSoundEnabled, playNotificationDing, getCustomSound, setCustomSound } from "../lib/notificationSound";

export default function MySignature() {
  const { user } = useAuth();
  const [form, setForm] = useState({ signature_html: "", photo_url: "", direct_phone: "", phone: "", notif_prefs: { escalation: true, contract_alert: true } });
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState({ enabled: false, qr: "", secret: "", code: "", backup: [] });
  const [soundOn, setSoundOn] = useState(() => isNotificationSoundEnabled());
  const [customSound, setCustomSoundState] = useState(() => getCustomSound("standard"));
  const [customSoundUrgent, setCustomSoundUrgentState] = useState(() => getCustomSound("urgent"));
  const soundFileRef = useRef();
  const soundFileRefUrgent = useRef();
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdShow, setPwdShow] = useState({ current: false, next: false, confirm: false });
  const [pwdBusy, setPwdBusy] = useState(false);

  const changePassword = async () => {
    if (!pwd.current || !pwd.next) { toast.error("Mot de passe actuel et nouveau requis"); return; }
    if (pwd.next.length < 8) { toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("La confirmation ne correspond pas au nouveau mot de passe"); return; }
    if (pwd.current === pwd.next) { toast.error("Le nouveau mot de passe doit être différent de l'actuel"); return; }
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next, confirm_password: pwd.confirm });
      toast.success("Mot de passe modifié");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setPwdBusy(false); }
  };

  const uploadCustomSound = (file, variant = "standard") => {
    if (!file) return;
    if (!/^audio\//i.test(file.type)) {
      _toast.error("Format non supporté — choisissez un fichier audio (mp3, wav, ogg)");
      return;
    }
    if (file.size > 500 * 1024) {
      _toast.error("Fichier trop volumineux — max 500 Ko (garder le son court)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setCustomSound(dataUrl, file.name, variant);
      if (variant === "urgent") setCustomSoundUrgentState({ url: dataUrl, name: file.name });
      else setCustomSoundState({ url: dataUrl, name: file.name });
      _toast.success(`Son ${variant === "urgent" ? "URGENT" : "standard"} chargé : ${file.name}`);
    };
    reader.onerror = () => _toast.error("Lecture du fichier échouée");
    reader.readAsDataURL(file);
  };

  const clearCustomSound = (variant = "standard") => {
    setCustomSound("", "", variant);
    if (variant === "urgent") setCustomSoundUrgentState({ url: "", name: "" });
    else setCustomSoundState({ url: "", name: "" });
    _toast.success("Son par défaut restauré");
  };

  const fileRef = useRef();

  const loadMe = () => api.get("/auth/me").then((r) => {
    setForm({
      signature_html: r.data.signature_html || "",
      photo_url: r.data.photo_url || "",
      direct_phone: r.data.direct_phone || "",
      phone: r.data.phone || "",
      notif_prefs: r.data.notif_prefs || { escalation: true, contract_alert: true },
    });
    setTwofa((s) => ({ ...s, enabled: !!r.data.totp_enabled }));
  });
  useEffect(() => { loadMe(); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.patch("/auth/me", form); toast.success("Enregistré"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/users/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: r.data.photo_url + `?t=${Date.now()}` }));
      toast.success("Photo mise à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
  };

  const start2fa = async () => {
    const r = await api.post("/auth/2fa/setup");
    setTwofa((s) => ({ ...s, qr: r.data.qr_png_base64, secret: r.data.secret, code: "" }));
  };
  const verify2fa = async () => {
    try {
      const r = await api.post("/auth/2fa/verify", { code: twofa.code });
      toast.success("2FA activée");
      setTwofa({ enabled: true, qr: "", secret: "", code: "", backup: r.data.backup_codes || [] });
      loadMe();
    } catch (e) { toast.error(e.response?.data?.detail || "Code invalide"); }
  };
  const disable2fa = async () => {
    const code = window.prompt("Entrez un code de votre application pour confirmer"); if (!code) return;
    try { await api.post("/auth/2fa/disable", { code }); toast.success("2FA désactivée");
      setTwofa({ enabled: false, qr: "", secret: "", code: "", backup: [] }); loadMe();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const regenBackup = async () => {
    const code = window.prompt("Entrez un code 2FA actuel pour régénérer les codes de secours"); if (!code) return;
    try {
      const r = await api.post("/auth/2fa/backup-codes/regenerate", { code });
      setTwofa((s) => ({ ...s, backup: r.data.backup_codes }));
      toast.success("Codes régénérés — enregistrez-les !");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const preview = `
    <table style="width:100%;font-size:13px;color:#334155;"><tr>
      ${form.photo_url ? `<td style="width:64px;padding-right:12px;vertical-align:top"><img src="${form.photo_url}" style="width:56px;height:56px;border-radius:999px;object-fit:cover"/></td>` : ""}
      <td style="vertical-align:top">
        <div style="font-weight:600;color:#0f172a">${user?.full_name || ""}</div>
        <div style="color:#64748b;font-size:12px">${(user?.role || "").toUpperCase()}</div>
        ${form.direct_phone ? `<div style="margin-top:4px">Direct : <b>${form.direct_phone}</b></div>` : ""}
        ${form.phone ? `<div>Tel : ${form.phone}</div>` : ""}
        <div>${user?.email || ""}</div>
        ${form.signature_html ? `<div style="margin-top:6px">${form.signature_html}</div>` : ""}
      </td>
    </tr></table>`;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="label-caps">Profil</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Mon profil</h1>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><UserIcon size={20}/> Signature</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Photo de profil</Label>
            <div className="flex items-center gap-3 mt-1">
              <div className="w-16 h-16 rounded-full bg-muted overflow-hidden flex items-center justify-center border">
                {form.photo_url
                  ? <img src={form.photo_url} alt="" className="w-full h-full object-cover"/>
                  : <UserIcon size={22} className="text-muted-foreground"/>}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value=""; }}/>
              <Button variant="secondary" onClick={() => fileRef.current?.click()} data-testid="ms-photo-upload"><Camera size={14} className="mr-2"/> Envoyer une photo</Button>
            </div>
          </div>
          <div>
            <Label>Ligne directe</Label>
            <Input data-testid="ms-direct" value={form.direct_phone} onChange={(e) => setForm({...form, direct_phone: e.target.value})} placeholder="+33 1 23 45 67 89"/>
            <Label className="mt-3 block">Téléphone mobile / standard</Label>
            <Input data-testid="ms-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/>
          </div>
          <div className="md:col-span-2">
            <Label>Message personnel (HTML)</Label>
            <RichEditor value={form.signature_html} onChange={(v) => setForm({...form, signature_html: v})} minHeight={140}/>
          </div>
          <div className="md:col-span-2">
            <Label>Aperçu</Label>
            <div className="border rounded-md p-4 bg-muted/30 mt-1"><RichView html={preview}/></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Bell size={20}/> Préférences de notifications</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div><div className="font-medium">Escalades SLA</div><div className="text-xs text-muted-foreground">Recevoir la cloche quand un ticket approche de son SLA.</div></div>
            <Switch checked={!!form.notif_prefs.escalation} onCheckedChange={(v) => setForm({...form, notif_prefs: {...form.notif_prefs, escalation: v}})} data-testid="notif-esc"/>
          </div>
          <div className="flex items-center justify-between">
            <div><div className="font-medium">Dépassement de contrat</div><div className="text-xs text-muted-foreground">Recevoir la cloche quand un contrat atteint son seuil.</div></div>
            <Switch checked={!!form.notif_prefs.contract_alert} onCheckedChange={(v) => setForm({...form, notif_prefs: {...form.notif_prefs, contract_alert: v}})} data-testid="notif-contract"/>
          </div>
          <div className="flex items-center justify-between pt-3 border-t">
            <div>
              <div className="font-medium flex items-center gap-2"><SpeakerHigh size={16}/> Son de notification</div>
              <div className="text-xs text-muted-foreground">Petit "ding" quand un nouveau ticket arrive (ou un update pour les clients).</div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={playNotificationDing} data-testid="notif-sound-test">Tester</Button>
              <Switch checked={soundOn} onCheckedChange={(v) => { setSoundOn(v); setNotificationSoundEnabled(v); }} data-testid="notif-sound"/>
            </div>
          </div>
          <div className="pl-6 space-y-4">
            <div>
              <Label className="text-xs">🔔 Son standard (nouveau ticket normal)</Label>
              <input ref={soundFileRef} type="file" accept="audio/*" className="hidden"
                onChange={(e) => { uploadCustomSound(e.target.files?.[0], "standard"); e.target.value = ""; }}
                data-testid="notif-sound-file"/>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Button size="sm" variant="secondary" onClick={() => soundFileRef.current?.click()} data-testid="notif-sound-upload">
                  <UploadSimple size={14} className="mr-1.5"/> Charger
                </Button>
                <Button size="sm" variant="ghost" onClick={() => playNotificationDing()} data-testid="notif-sound-test-std">Tester</Button>
                {customSound.url ? (
                  <>
                    <span className="text-xs text-emerald-700 dark:text-emerald-400 truncate max-w-xs" data-testid="notif-sound-name">🎵 {customSound.name || "Son personnalisé"}</span>
                    <Button size="sm" variant="ghost" onClick={() => clearCustomSound("standard")} data-testid="notif-sound-clear" title="Restaurer le son par défaut"><X size={14}/></Button>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">Défaut : ding synthétique 2-tons doux</span>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs">🚨 Son URGENT (ticket urgent ou critique)</Label>
              <input ref={soundFileRefUrgent} type="file" accept="audio/*" className="hidden"
                onChange={(e) => { uploadCustomSound(e.target.files?.[0], "urgent"); e.target.value = ""; }}
                data-testid="notif-sound-file-urgent"/>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Button size="sm" variant="secondary" onClick={() => soundFileRefUrgent.current?.click()} data-testid="notif-sound-upload-urgent">
                  <UploadSimple size={14} className="mr-1.5"/> Charger
                </Button>
                <Button size="sm" variant="ghost" onClick={() => playNotificationDing({ urgent: true })} data-testid="notif-sound-test-urgent">Tester</Button>
                {customSoundUrgent.url ? (
                  <>
                    <span className="text-xs text-red-700 dark:text-red-400 truncate max-w-xs" data-testid="notif-sound-name-urgent">🚨 {customSoundUrgent.name || "Son urgent"}</span>
                    <Button size="sm" variant="ghost" onClick={() => clearCustomSound("urgent")} data-testid="notif-sound-clear-urgent" title="Restaurer le son urgent par défaut"><X size={14}/></Button>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">Défaut : alarme synthétique 3-tons aiguë</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><Key size={20}/> Mot de passe</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">Modifiez votre mot de passe. Il doit contenir au moins 8 caractères et être différent de l&apos;actuel.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Mot de passe actuel</Label>
              <div className="relative">
                <Input type={pwdShow.current ? "text" : "password"} value={pwd.current} onChange={(e) => setPwd({...pwd, current: e.target.value})} autoComplete="current-password" data-testid="pwd-current"/>
                <button type="button" onClick={() => setPwdShow({...pwdShow, current: !pwdShow.current})} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1} data-testid="pwd-current-toggle">
                  {pwdShow.current ? <EyeSlash size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <div>
              <Label>Nouveau mot de passe</Label>
              <div className="relative">
                <Input type={pwdShow.next ? "text" : "password"} value={pwd.next} onChange={(e) => setPwd({...pwd, next: e.target.value})} autoComplete="new-password" data-testid="pwd-next"/>
                <button type="button" onClick={() => setPwdShow({...pwdShow, next: !pwdShow.next})} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1} data-testid="pwd-next-toggle">
                  {pwdShow.next ? <EyeSlash size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <div>
              <Label>Confirmer</Label>
              <div className="relative">
                <Input type={pwdShow.confirm ? "text" : "password"} value={pwd.confirm} onChange={(e) => setPwd({...pwd, confirm: e.target.value})} autoComplete="new-password" data-testid="pwd-confirm"/>
                <button type="button" onClick={() => setPwdShow({...pwdShow, confirm: !pwdShow.confirm})} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1} data-testid="pwd-confirm-toggle">
                  {pwdShow.confirm ? <EyeSlash size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={changePassword} disabled={pwdBusy || !pwd.current || !pwd.next || !pwd.confirm} data-testid="pwd-save">
              <Key size={14} className="mr-2"/>{pwdBusy ? "…" : "Modifier le mot de passe"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display flex items-center gap-2"><ShieldCheck size={20}/> Double authentification (2FA)</CardTitle></CardHeader>
        <CardContent>
          {twofa.enabled ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div><b className="text-emerald-600">Activée</b> — utilisez votre application 2FA (ou un code de secours) à chaque connexion.</div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={regenBackup} data-testid="ms-2fa-regen">Régénérer les codes</Button>
                  <Button variant="secondary" onClick={disable2fa} data-testid="ms-2fa-disable">Désactiver</Button>
                </div>
              </div>
              {twofa.backup?.length > 0 && (
                <div className="rounded-md border-2 border-orange-500 bg-orange-50 dark:bg-orange-950/20 p-4">
                  <div className="font-semibold text-orange-700 dark:text-orange-300 mb-2">⚠ Notez ces 10 codes de secours (affichés une seule fois)</div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mono text-sm" data-testid="backup-codes">
                    {twofa.backup.map((c) => <div key={c} className="bg-white dark:bg-black rounded px-2 py-1 border text-center">{c}</div>)}
                  </div>
                  <Button size="sm" variant="secondary" className="mt-3" onClick={() => { navigator.clipboard.writeText(twofa.backup.join("\n")); toast.success("Codes copiés"); }}>Copier</Button>
                </div>
              )}
            </div>
          ) : twofa.qr ? (
            <div className="space-y-3">
              <p className="text-sm">Scannez ce QR code avec Google Authenticator, Authy ou 1Password, puis entrez le code affiché :</p>
              <div className="flex items-center gap-4">
                <img src={`data:image/png;base64,${twofa.qr}`} alt="QR" className="w-40 h-40 border rounded-md"/>
                <div className="text-xs mono text-muted-foreground break-all">Clé manuelle : {twofa.secret}</div>
              </div>
              <div className="flex items-end gap-2 max-w-xs">
                <div className="flex-1"><Label>Code à 6 chiffres</Label>
                  <Input data-testid="ms-2fa-code" value={twofa.code} onChange={(e) => setTwofa({...twofa, code: e.target.value.replace(/[^0-9]/g,"")})} maxLength={6}/>
                </div>
                <Button onClick={verify2fa} data-testid="ms-2fa-verify">Vérifier</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">Pas encore activée — protégez votre compte en 30 secondes.</div>
              <Button onClick={start2fa} data-testid="ms-2fa-start"><ShieldCheck size={14} className="mr-2"/> Activer la 2FA</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="ms-save"><FloppyDisk size={16} className="mr-2"/>{busy ? "…" : "Enregistrer"}</Button>
      </div>
    </div>
  );
}
